import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Clock, Banknote } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import { getLeaveBalanceReport } from '../../services/leaveBalanceReportService';
import { getLeaveAccrualContext } from '../../services/leaveVariationService';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import { formatDate, parseTableParams } from '../../utils/Utils';
import { buildReportLabels } from '../../utils/reportLabels';
import BreakdownModal from './BreakdownModal';
import PersonCell from '../../components/ui/PersonCell';
import { FilterCard, FilterField, SearchField, ClearFilters } from './ReportFilterBar';

// Kapsam hatası (astsız + yetkisiz) — toast yerine sayfada sakin bir açıklama.
const ScopeHint = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

// Negatif kalan = borçlanılmış izin.
const Kalan = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ $negative, theme }) => ($negative ? theme.colors.auxiliar.danger : theme.colors.text.primary)};
`;

const ExpiringHint = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export default function LeaveBalanceReportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [scopeError, setScopeError] = useState(null);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 25 });
  const [page, setPage] = useState({ pageNumber: 1, pageSize: 25 });
  const [sort, setSort] = useState({ sortField: 'fullName', sortDirection: 'asc' });
  const [departmentId, setDepartmentId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [name, setName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [breakdownRow, setBreakdownRow] = useState(null);
  const history = useHistory();
  // "Nakde Çevrilecek İzinler" bağlantısı yalnız kiracıda devir tavanı / süresi olan tür
  // varken görünür — Türkiye kiracılarının neredeyse tamamında rapor tanım gereği boştur.
  const [hasCarryOverTypes, setHasCarryOverTypes] = useState(false);

  useEffect(() => {
    getLeaveAccrualContext().then((res) => setHasCarryOverTypes(!!res?.data?.data?.hasCarryOverTypes));
  }, []);

  // Süzgeç ya da sıralama değişince 1. sayfaya dönülür — AYNI olayda (tek render, tek istek). Dönülmezse 3. sayfadayken
  // 9 kişilik bir departman seçilince sunucudan 51-75. satırlar istenir ve tablo boş gelir (2026-10-20, vMind).
  // İşlevsel güncelleme: zaten 1. sayfadaysa nesne değişmez, gereksiz istek atılmaz.
  const ilkSayfa = useCallback(() => setPage((p) => (p.pageNumber === 1 ? p : { ...p, pageNumber: 1 })), []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name.trim());
      ilkSayfa();
    }, 400);
    return () => clearTimeout(timer);
  }, [name, ilkSayfa]);

  const filterParams = useMemo(
    () => ({ departmentId, locationId, name: debouncedName, ...sort }),
    [departmentId, locationId, debouncedName, sort],
  );

  const queryParams = useMemo(() => ({ ...filterParams, ...page }), [filterParams, page]);

  // Art arda süzgeç değişikliklerinde yanıtlar sırasız dönebilir; yalnız EN SON isteğin yanıtı tabloya yazılır.
  const sonIstek = useRef(0);

  const fetchData = useCallback((params) => {
    const istek = sonIstek.current + 1;
    sonIstek.current = istek;
    setFetching(true);
    getLeaveBalanceReport(params)
      .then((res) => {
        if (istek !== sonIstek.current) return;
        // api.js GET'leri reject etmez — sinyal res.data.status.
        if (!res.data?.status) {
          setScopeError(res.data?.message || 'leaveBalanceReport.noScope');
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: params.pageSize || 25 });
          return;
        }
        setScopeError(null);
        const result = res.data?.data;
        const items = result?.data || [];
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || 25,
        });
      })
      .finally(() => { if (istek === sonIstek.current) setFetching(false); });
  }, []);

  useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleTableChange = useCallback((action, tableState) => {
    if (action === 'sort') {
      const { name: colName, direction } = tableState.sortOrder || {};
      if (colName) {
        setSort({ sortField: colName, sortDirection: direction || 'asc' });
        ilkSayfa();
      }
      return;
    }
    const parsed = parseTableParams(tableState, []);
    setPage({ pageNumber: parsed.pageNumber || 1, pageSize: parsed.pageSize || 25 });
  }, [ilkSayfa]);

  const handleDepartment = useCallback((v) => { setDepartmentId(v); ilkSayfa(); }, [ilkSayfa]);
  const handleLocation = useCallback((v) => { setLocationId(v); ilkSayfa(); }, [ilkSayfa]);
  const clearFilters = useCallback(() => {
    setDepartmentId(null);
    setLocationId(null);
    setName('');
    setDebouncedName('');
    ilkSayfa();
  }, [ilkSayfa]);

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await getDepartments(params);
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchLocations = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await getLocations(params);
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const numCell = (v) => (v === null || v === undefined ? '-' : Number(v));

  const columns = useMemo(() => [
    { name: 'fullName', label: t('leaveBalanceReport.employee'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.profilePicture} />
      ), filter: false, sort: true } },
    { name: 'departmentName', label: t('leaveBalanceReport.department'), options: { filter: false, sort: true, customBodyRender: (v) => v || '-' } },
    { name: 'titleName', label: t('leaveBalanceReport.jobTitle'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'employmentDate', label: t('leaveBalanceReport.employmentDate'), options: { filter: false, sort: true, customBodyRender: (v) => (v ? formatDate(v, lang) : '-') } },
    { name: 'totalEntitlement', label: t('leaveBalanceReport.entitlement'), options: { filter: false, sort: true, customBodyRender: numCell } },
    { name: 'totalUsed', label: t('leaveBalanceReport.used'), options: { filter: false, sort: true, customBodyRender: numCell } },
    { name: 'totalPending', label: t('leaveBalanceReport.pending'), options: { filter: false, sort: true, customBodyRender: numCell } },
    {
      name: 'remaining',
      label: t('leaveBalanceReport.remaining'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Kalan $negative={Number(v) < 0}>
              {numCell(v)}
              {row?.expiring && (
                <ExpiringHint title={t('leaveBalanceReport.expiringTooltip')}>
                  <Clock size={13} />
                </ExpiringHint>
              )}
            </Kalan>
          );
        },
      },
    },
  ], [t, lang, data]);

  // AKM POC §7.1 — sunucu tarafı Excel / PDF: o anki süzgeçler (departman, lokasyon, ad) ve sıralama, TAM veri.
  const getExportLabels = useCallback(
    () => buildReportLabels(t, columns, t('leaveBalanceReport.title')),
    [t, columns],
  );

  return (
    <>
      <FilterCard>
        <FilterField>
          <AsyncGenericSelect
            compact
            label={t('leaveBalanceReport.department')}
            value={departmentId}
            onChange={handleDepartment}
            onSearch={searchDepartments}
          />
        </FilterField>
        <FilterField>
          <AsyncGenericSelect
            compact
            label={t('leaveBalanceReport.location')}
            value={locationId}
            onChange={handleLocation}
            onSearch={searchLocations}
          />
        </FilterField>
        <FilterField $w="260px">
          <SearchField value={name} onChange={setName} placeholder={t('leaveBalanceReport.searchName')} />
        </FilterField>
        <ClearFilters visible={!!(departmentId || locationId || name)} onClick={clearFilters} />
      </FilterCard>

      {scopeError ? (
        <ScopeHint>{t(scopeError)}</ScopeHint>
      ) : (
        <DataTable
          title={t('leaveBalanceReport.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onTableChanged={handleTableChange}
          onRowClick={(row) => setBreakdownRow(row || null)}
          disableFilters
          download={false}
          toolbarActions={(
            <>
              {hasCarryOverTypes && (
                <Button variant="outline" onClick={() => history.push('/leave-balance-report/payouts')}>
                  <Banknote size={16} /> {t('leavePayoutReport.title')}
                </Button>
              )}
              <ReportExportButtons url="/v1/leave-balance-report/export" params={filterParams} getLabels={getExportLabels} />
            </>
          )}
        />
      )}

      <BreakdownModal row={breakdownRow} onClose={() => setBreakdownRow(null)} />
    </>
  );
}
